export type ExerciseTypeAvailability = "onboarding" | "program" | "both";

export type ExerciseTypeTier = "umbrella" | "primary" | "secondary";

export type ExerciseTypeOption = {
  id: string;
  label: string;
  availableIn: ExerciseTypeAvailability;
  isExtended: boolean;
  tier: ExerciseTypeTier;
};

// Source of truth for all exercise types across:
// - Client onboarding (simple list)
// - Trainer programs (extended + legacy list for backward compatibility)
export const exerciseTypeCatalog: ExerciseTypeOption[] = [
  // Onboarding (legacy/simple) options — umbrella tier
  { id: "strength-training", label: "Strength Training", availableIn: "both", isExtended: false, tier: "umbrella" },
  { id: "cardio", label: "Cardio", availableIn: "both", isExtended: false, tier: "umbrella" },
  { id: "mobility", label: "Mobility", availableIn: "program", isExtended: true, tier: "umbrella" },
  { id: "running", label: "Running", availableIn: "both", isExtended: false, tier: "umbrella" },
  { id: "cycling", label: "Cycling", availableIn: "both", isExtended: false, tier: "umbrella" },
  { id: "swimming", label: "Swimming", availableIn: "both", isExtended: false, tier: "umbrella" },
  { id: "group-classes", label: "Group Classes", availableIn: "both", isExtended: false, tier: "umbrella" },
  { id: "sports", label: "Sport", availableIn: "both", isExtended: false, tier: "umbrella" },
  { id: "hiit", label: "HIIT", availableIn: "both", isExtended: false, tier: "umbrella" },

  // Extended (trainer/program) options — primary tier
  { id: "mobility-foam-rolling", label: "Mobility - Foam Rolling", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "mobility-dynamic-mobility", label: "Mobility - Dynamic Mobility", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "mobility-stretches", label: "Mobility - Stretches", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "stability", label: "Stability", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "resistance-strength", label: "Resistance (strength)", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "resistance-power", label: "Resistance (power)", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "resistance-hypertrophy", label: "Resistance (hypertrophy)", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "resistance-muscular-endurance", label: "Resistance (muscular endurance)", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "cardiovascular-zone-1-recovery", label: "Cardiovascular (Zone 1 – recovery)", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "cardiovascular-zone-2-steady-state", label: "Cardiovascular (Zone 2 – steady-state)", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "cardiovascular-zone-3-tempo-aerobic-power", label: "Cardiovascular (Zone 3 - Tempo / Aerobic Power)", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "cardiovascular-zone-4-threshold-lactate-threshold", label: "Cardiovascular (Zone 4 - Threshold / Lactate Threshold)", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "cardiovascular-zone-5-vo2max-anaerobic-power", label: "Cardiovascular (Zone 5 - VO₂max / Anaerobic Power)", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "hiit-v1", label: "HIIT Version 1", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "hiit-v2", label: "HIIT Version 2", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "hiit-v3", label: "HIIT Version 3", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "hiit-v4", label: "HIIT Version 4", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "agility", label: "Agility", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "plyometrics", label: "Plyometrics", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "speed-development", label: "Speed Development", availableIn: "program", isExtended: true, tier: "primary" },
  { id: "proprioception-balance", label: "Balance / Proprioception", availableIn: "program", isExtended: true, tier: "primary" },
];

export const onboardingExerciseTypeOptions = exerciseTypeCatalog
  .filter((t) => t.availableIn === "onboarding" || t.availableIn === "both")
  .map(({ id, label }) => ({ id, label }));

export const programExerciseTypeOptions = exerciseTypeCatalog
  .filter((t) => t.availableIn === "program" || t.availableIn === "both")
  .map(({ id, label }) => ({ id, label }));

export const exerciseTypeLabelById: Record<string, string> = Object.fromEntries(
  exerciseTypeCatalog.map((t) => [t.id, t.label])
);
